



















































































import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator'
import { Template, Question, TemplateField, TemplateValue, Asset, QuestionType } from '@shared/types/database'
import { Icon, Button, SelectOption, Select } from '@alpha/ink'
import TemplateQuestion from './TemplateQuestion.vue'
import QuestionHeader from '@/components/QuestionHeader.vue'
import QuestionWrapper from '@/components/QuestionWrapper.vue'
import AnimatedContainer from '@/components/AnimatedContainer.vue'

import { PermissionsObject } from '@shared/types/graphql'
import { Location } from '@/components/Templates/TemplateManager.vue'

export interface QuestionTypeOption {
  title: 'Rate' | 'Single-Select' | 'Multi-Select',
  value: QuestionType
}

@Component({
  components: {
    Icon,
    TemplateQuestion,
    Select,
    AnimatedContainer,
    QuestionHeader,
    QuestionWrapper,
    Button
  }
})
export default class TemplateQuestions extends Vue {
  @Prop({ default: true }) readonly editMode!: boolean
  @Prop({ default: false }) readonly showWarningStyles!: boolean
  @Prop({ default: true }) readonly showErrorStyles!: boolean
  @Prop({ default: () => ({}) }) readonly assets!: Record<string, Asset>
  @Prop({ required: true }) readonly templateQuestions!: Question[]
  @Prop({ required: true }) readonly template!: Template
  @Prop({ required: true }) readonly templateFields!: Record<string, TemplateField>
  @Prop({ required: true }) readonly templateFieldValues!: Record<string, TemplateValue>
  @Prop() readonly acl!: PermissionsObject
  @Prop() readonly location!: Location

  @Ref('question') readonly templateQuestionComponents!: TemplateQuestion[]

  initialQuestionGrouping = {} as Record<string, string[]>
  customQuestionButtonHover = false
  show: Record<string, boolean> = {
    customQuestionBuilder: false
  }

  get questionTypeOptions (): SelectOption[] {
    return [
      { label: 'Rate', value: 'rate-only' },
      { label: 'Single-Select', value: 'single-select' },
      { label: 'Multi-Select', value: 'multi-select' },
      { label: 'Text Response', value: 'textarea' }
    ]
  }

  get customQuestionButtonTooltip () {
    if (this.hasMaxCustomQuestions) {
      return 'Three custom questions max'
    }
    if (this.hasMaxQuestions) {
      return 'Ten questions max'
    }
    return false
  }

  get customQuestionButtonDisabled () {
    return this.hasMaxCustomQuestions || this.hasMaxQuestions
  }

  get hasMaxCustomQuestions () {
    return this.templateQuestions.filter(q => q.custom).length >= 3
  }

  get hasMaxQuestions () {
    return this.templateQuestions.length >= 10
  }

  get canModifyTemplateQuestions () {
    return this.acl ? !!this.acl.templateQuestionModification : false
  }

  @Watch('selectedTemplate')
  setInitialQuestionGrouping (newValue: Template, oldValue: Template) {
    if (newValue.id !== oldValue.id) {
      this.show.customQuestionBuilder = false
    }
    this.initialQuestionGrouping = this.groupQuestions()
  }

  customQuestionClicked () {
    if (this.customQuestionButtonDisabled) return
    this.show.customQuestionBuilder = true
    this.$nextTick(() => {
      (this.$refs.customQuestion as Vue).$el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    })
  }

  customQuestionTypeSelected (selectedOption: QuestionTypeOption) {
    this.addQuestion(selectedOption.value)
    this.show.customQuestionBuilder = false
    this.$nextTick(() => {
      (this.$refs.addCustomQuestion as HTMLElement).scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    })
  }

  groupQuestions () {
    const groupedQuestions = this.templateQuestions.reduce((acc, question) => {
      if (!('templateFields' in question)) return acc
      question.templateFields.forEach(field => {
        if (!field.id) return
        if (!acc[field.id]) acc[field.id] = []
        acc[field.id] = acc[field.id].concat([question.id])
      })
      return acc
    }, {} as Record<string, string[]>)
    return groupedQuestions
  }

  editableData (questionId: string) {
    const editableData = {} as Record<string, boolean>
    const groupedQuestions = this.groupQuestions()
    Object.keys(groupedQuestions).forEach(dataField => {
      if (groupedQuestions[dataField].indexOf(questionId) === 0) {
        editableData[dataField] = true
      }
    })
    return editableData
  }

  isOnly (questionId: string) {
    let isOnly
    const groupedQuestions = this.groupQuestions()
    Object.keys(groupedQuestions).forEach(dataField => {
      const onlyQuestionLeft = groupedQuestions[dataField].length === 1 && groupedQuestions[dataField].includes(questionId)
      if (this.initiallyDependent(dataField) && onlyQuestionLeft) {
        isOnly = true
      }
    })
    return !!isOnly
  }

  initiallyDependent (dataField: string) {
    return this.initialQuestionGrouping[dataField] && this.initialQuestionGrouping[dataField].length > 1
  }

  validate () {
    const valid = this.templateQuestionComponents.map(question => {
      return question.validate()
    })
    return valid.every(value => value)
  }

  clearValidation () {
    this.templateQuestionComponents.forEach(question => {
      question.clearValidation()
    })
  }

  addQuestion (payload: Question['questionType']) {
    this.$emit('addQuestion', payload)
  }
}
